import { ApolloProvider } from '@apollo/client';
import { Rights } from '@dokucoach/common-types';
import { createTheme } from '@material-ui/core/styles';
import AdapterDateFns from '@material-ui/lab/AdapterDateFns';
import LocalizationProvider from '@material-ui/lab/LocalizationProvider';
import { StylesProvider, ThemeProvider as MuiThemeProvider } from '@material-ui/styles';
import AuthManager from 'components/AuthManager';
import CircularLoading from 'components/CircularLoading';
import ConfirmationModal from 'components/ConfirmationModal';
import { useNewVersionAvailability } from 'hooks/useNewVersionAvailability';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HashRouter } from 'react-router-dom';
import theme from 'style/theme';
import { ThemeProvider } from 'styled-components';
import shallow from 'zustand/shallow';
import SnackbarContext from './components/Snackbar/context';
import routePaths from './constants/route-paths';
import Router from './Router';
import apolloClient from './utils/apolloClient';
import './utils/i18n';
import { Version } from './Version';

const customizedTheme = createTheme({
  ...theme,
  typography: {
    fontFamily: ['"Poppins"', 'sans-serif'].join(','),
  },
});

const App = () => {
  const [isNewVersionAvailable, setIsNewVersionAvailable] = useNewVersionAvailability(
    (state) => [state.isNewVersionAvailable, state.setIsNewVersionAvailable],
    shallow,
  );
  const { t } = useTranslation();
  const privatePaths = [
    { path: routePaths.HOME, permissions: [Rights.CARE.READ] },
    {
      path: routePaths.BASE_DATA,
      permissions: [
        Rights.BASE_DATA.DOCTORS.READ,
        Rights.BASE_DATA.SUPERVISOR.READ,
        Rights.RESIDENTS.READ,
      ],
    },
  ];

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('newVersion')) {
      url.searchParams.delete('newVersion');
      window.location.href = url.toString();
    }
  });

  const onConfirmResponse = useCallback(
    (confirmed: boolean) => {
      if (confirmed && isNewVersionAvailable) {
        window.location.href = new URL(
          `?newVersion=${encodeURIComponent(isNewVersionAvailable)}`,
          window.location.origin,
        ).toString();
      }
      setIsNewVersionAvailable(null);
    },
    [isNewVersionAvailable, setIsNewVersionAvailable],
  );

  return (
    // Warning is annoyed us from the React Material UI
    // https://github.com/mui-org/material-ui/issues/13394
    <React.StrictMode>
      <StylesProvider injectFirst>
        <MuiThemeProvider theme={customizedTheme}>
          <ThemeProvider theme={theme}>
            <React.Suspense fallback={<CircularLoading />}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <HashRouter>
                  <ApolloProvider client={apolloClient}>
                    <SnackbarContext>
                      <AuthManager publicPath={routePaths.LOGIN} privatePaths={privatePaths}>
                        <Router />
                        <ConfirmationModal
                          show={isNewVersionAvailable !== null}
                          hideCancelButton
                          text={t('confirmation_new_version_reload')}
                          yesButtonTextKey={t('confirmation_ok')}
                          onResponse={onConfirmResponse}
                        />
                      </AuthManager>
                    </SnackbarContext>
                  </ApolloProvider>
                </HashRouter>
              </LocalizationProvider>
            </React.Suspense>
          </ThemeProvider>
        </MuiThemeProvider>
      </StylesProvider>
      <Version />
    </React.StrictMode>
  );
};

export default App;
